import type { MenuItem } from '@tyndo/ui-kit';

/**
 * Collapse a sidebar group that contains exactly ONE destination into that
 * destination (docs/latest-analysis-ui-ux.md UX-A1).
 *
 * Sixteen modules declare a root menu entry plus a single child. The shell
 * rendered that as an expandable group holding one item, so reaching the
 * module's only console cost an expand and then a click — an accordion whose
 * whole content is one row, sixteen times over in the sidebar.
 *
 * The surviving row keeps the PARENT's label, because that is the module name
 * an operator scans a band for, and takes the CHILD's id, because that is the
 * destination its author declared. `handleMenuItemClick` resolves navigation
 * from the id through `menuRegistry`, so pointing at the child lands exactly
 * where expanding and clicking used to land.
 *
 * This merges nothing and hides nothing: the same destinations exist and the
 * count an operator can reach is unchanged. It removes a click that never had
 * a choice in it.
 *
 * Lives in its own file because AppShell.tsx sits just under the 1000-line
 * ceiling `lint:view-loc` enforces, and inlining this pushed it to 1018.
 */
export function collapseSingletonGroups(items: MenuItem[]): MenuItem[] {
  return items.map((item) => {
    const only = item.children?.length === 1 ? item.children[0] : undefined;
    if (!only) return item;
    return { ...item, id: only.id, badge: only.badge ?? item.badge, children: undefined };
  });
}
